import { useCallback } from 'react';
import { usePostUserMutation, usePutUserMutation } from '@/api/v2/user';
import { useNotification } from '@starjack/features/notifications';
import { User, CreateUser, isOfTypeUser } from '@/models/user';

type UseUpsertUserProps = {
    onSuccess?: (user: User) => void;
    onError?: (error: unknown) => void;
};

export const useUpsertUser = ({
    onSuccess,
    onError,
}: UseUpsertUserProps = {}) => {
    const [putUser, { isLoading: putLoading }] = usePutUserMutation();
    const [postUser, { isLoading: postLoading }] = usePostUserMutation();
    const { addNotification } = useNotification();

    const upsertUser = useCallback(
        (user: User | CreateUser) => {
            const mutationHandler = isOfTypeUser(user) ? putUser : postUser;

            return mutationHandler(user)
                .unwrap()
                .then((savedUser) => {
                    addNotification({
                        message: 'Speichern erfolgreich',
                        type: 'success',
                    });

                    onSuccess?.(savedUser);
                    return savedUser;
                })
                .catch((reason) => {
                    console.error(reason);

                    let message =
                        'Fehler beim Speichern. Versuchen Sie es später noch einmal';
                    if (
                        reason?.data?.errors?.[0]?.error ===
                        'username_already_exists'
                    ) {
                        message =
                            'Ein User mit diesem Benutzername existiert bereits. Bitte wählen Sie einen anderen';
                    }

                    addNotification({
                        message,
                        type: 'error',
                    });

                    onError?.(reason);
                    throw reason;
                });
        },
        [putUser, postUser, addNotification, onSuccess, onError]
    );

    return {
        upsertUser,
        isLoading: putLoading || postLoading,
    };
};
